import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  TravelExplore as TourIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../components/layout/AdminLayout';
import { toursAPI, apiCall } from '../services/api';

const ToursManagement = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    destination: '',
    start_date: '',
    end_date: '',
    departure_flight_info: '',
    return_flight_info: '',
    makkah_hotel: '',
    madina_hotel: '',
    total_groups: 0,
    total_buses: 0,
    emergency_contact: '',
    guide_info: '',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTour, setSelectedTour] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, tour: null, confirmText: '' });

  useEffect(() => {
    document.title = 'Tours - PIMS';
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      setLoading(true);
      const data = await apiCall(toursAPI.getAll);
      setTours(data.tours);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tour = null) => {
    if (tour) {
      setEditingTour(tour);
      setFormData({
        name: tour.name,
        destination: tour.destination,
        start_date: tour.start_date,
        end_date: tour.end_date,
        departure_flight_info: tour.departure_flight_info || '',
        return_flight_info: tour.return_flight_info || '',
        makkah_hotel: tour.makkah_hotel || '',
        madina_hotel: tour.madina_hotel || '',
        total_groups: tour.total_groups || 0,
        total_buses: tour.total_buses || 0,
        emergency_contact: tour.emergency_contact || '',
        guide_info: tour.guide_info || '',
      });
    } else {
      setEditingTour(null);
      setFormData({
        name: '',
        destination: '',
        start_date: '',
        end_date: '',
        departure_flight_info: '',
        return_flight_info: '',
        makkah_hotel: '',
        madina_hotel: '',
        total_groups: 0,
        total_buses: 0,
        emergency_contact: '',
        guide_info: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTour(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingTour) {
        await apiCall(toursAPI.update, editingTour.id, formData);
      } else {
        await apiCall(toursAPI.create, formData);
      }
      handleCloseDialog();
      fetchTours();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMenuClick = (event, tour) => {
    setAnchorEl(event.currentTarget);
    setSelectedTour(tour);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTour(null);
  };

  const handleDelete = (tour) => {
    setDeleteDialog({ open: true, tour, confirmText: '' });
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!deleteDialog.tour) return;
    
    try {
      setLoading(true);
      const response = await apiCall(toursAPI.delete, deleteDialog.tour.id);
      
      // Show success message with details
      if (response.deleted) {
        const { tour, travelers, documents, files } = response.deleted;
        alert(`Successfully deleted tour "${deleteDialog.tour.name}"\n` +
              `• ${travelers} travelers removed\n` +
              `• ${documents} document records deleted\n` +
              `• ${files} files removed from server`);
      }
      
      setDeleteDialog({ open: false, tour: null, confirmText: '' });
      fetchTours();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ open: false, tour: null, confirmText: '' });
  };

  const handleStatusChange = async (tour, newStatus) => {
    try {
      await apiCall(toursAPI.updateStatus, tour.id, newStatus);
      fetchTours();
    } catch (err) {
      setError(err.message);
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 0 }}
          mb={3}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Tours Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              borderRadius: 2,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Add New Tour
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tours Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 800, md: 'auto' } }}>
              <TableHead 
                sx={{ 
                  backgroundColor: (theme) => 
                    theme.palette.mode === 'dark' 
                      ? theme.palette.background.paper 
                      : theme.palette.grey[50]
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Tour Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Destination</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Dates</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Travelers</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tours.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <TourIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography variant="body1" color="text.secondary">
                          No tours found. Create your first tour to get started.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  tours.map((tour) => (
                    <TableRow key={tour.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {tour.name}
                          </Typography>
                          {tour.makkah_hotel && (
                            <Typography variant="caption" color="text.secondary">
                              Hotels: {tour.makkah_hotel}
                              {tour.madina_hotel && ` • ${tour.madina_hotel}`}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={tour.destination} 
                          size="small" 
                          variant="outlined"
                          icon={<TourIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2">
                              {formatDate(tour.start_date)} - {formatDate(tour.end_date)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {tour.traveler_count || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={tour.status || 'active'} 
                          color={getStatusColor(tour.status)} 
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, tour)}
                          size="small"
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => { handleOpenDialog(selectedTour); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Edit Tour
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange(selectedTour, 'active')}>
            Mark as Active
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange(selectedTour, 'completed')}>
            Mark as Completed
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange(selectedTour, 'cancelled')}>
            Mark as Cancelled
          </MenuItem>
          <MenuItem 
            onClick={() => handleDelete(selectedTour)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete Tour
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={cancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <DeleteIcon />
              Confirm Tour Deletion
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                This action cannot be undone!
              </Typography>
              <Typography variant="body2">
                Deleting this tour will permanently remove:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>The tour "{deleteDialog.tour?.name}"</li>
                <li>All associated travelers</li>
                <li>All traveler documents and files</li>
                <li>All related database records</li>
              </Box>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please type the tour name to confirm deletion:
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              placeholder={`Type "${deleteDialog.tour?.name}" to confirm`}
              variant="outlined"
              size="small"
              onChange={(e) => setDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
              value={deleteDialog.confirmText || ''}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={cancelDelete} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              disabled={deleteDialog.confirmText !== deleteDialog.tour?.name || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {loading ? 'Deleting...' : 'Delete Tour'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingTour ? 'Edit Tour' : 'Add New Tour'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tour Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Destination"
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Makkah Hotel"
                  value={formData.makkah_hotel}
                  onChange={(e) => setFormData({...formData, makkah_hotel: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Madina Hotel"
                  value={formData.madina_hotel}
                  onChange={(e) => setFormData({...formData, madina_hotel: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Departure Flight"
                  value={formData.departure_flight_info}
                  onChange={(e) => setFormData({...formData, departure_flight_info: e.target.value})}
                  placeholder="24 Jul 2025 07:50:00"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Return Flight"
                  value={formData.return_flight_info}
                  onChange={(e) => setFormData({...formData, return_flight_info: e.target.value})}
                  placeholder="07 Aug 2025 12:45:00"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Groups"
                  type="number"
                  value={formData.total_groups}
                  onChange={(e) => setFormData({...formData, total_groups: parseInt(e.target.value) || 0})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Buses"
                  type="number"
                  value={formData.total_buses}
                  onChange={(e) => setFormData({...formData, total_buses: parseInt(e.target.value) || 0})}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Emergency Contact"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Guide Information"
                  multiline
                  rows={3}
                  value={formData.guide_info}
                  onChange={(e) => setFormData({...formData, guide_info: e.target.value})}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!formData.name || !formData.destination || !formData.start_date || !formData.end_date}
            >
              {editingTour ? 'Update Tour' : 'Create Tour'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default ToursManagement;